import { SessionMetadata, ChatMessage, ConversationSession } from '../types';

export class SessionManager {
    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    private getSessionsStorageKey(personaId: string): string {
        return `persona_sessions_${personaId}`;
    }

    private getSessionStorageKey(personaId: string, sessionId: string): string {
        return `session_${personaId}_${sessionId}`;
    }

    getSessions(personaId: string): SessionMetadata[] {
        try {
            const storageKey = this.getSessionsStorageKey(personaId);
            const storedSessions = localStorage.getItem(storageKey);
            
            if (!storedSessions) {
                return [];
            }

            const sessions: SessionMetadata[] = JSON.parse(storedSessions);
            return sessions.sort((a, b) => b.updated_at - a.updated_at);
        } catch (error) {
            console.error('Error getting sessions:', error);
            return [];
        }
    }

    createSession(personaId: string, title?: string): SessionMetadata {
        const sessionId = this.generateSessionId();
        const now = Date.now();
        
        const session: SessionMetadata = {
            id: sessionId,
            personaId,
            title: title || 'New Conversation',
            created_at: now,
            updated_at: now,
            message_count: 0,
            last_message_preview: ''
        };

        const sessions = this.getSessions(personaId);
        sessions.push(session);
        
        const storageKey = this.getSessionsStorageKey(personaId);
        localStorage.setItem(storageKey, JSON.stringify(sessions));

        localStorage.setItem('active_persona', personaId);
        localStorage.setItem('active_session', sessionId);

        return session;
    }

    getSession(personaId: string, sessionId: string): ConversationSession | null {
        try {
            const sessions = this.getSessions(personaId);
            const metadata = sessions.find(s => s.id === sessionId);
            
            if (!metadata) {
                return null;
            }

            const sessionStorageKey = this.getSessionStorageKey(personaId, sessionId);
            const storedMessages = localStorage.getItem(sessionStorageKey);
            const messages: ChatMessage[] = storedMessages ? JSON.parse(storedMessages) : [];

            return { metadata, messages };
        } catch (error) {
            console.error('Error getting session:', error);
            return null;
        }
    }

    saveMessage(personaId: string, sessionId: string, message: ChatMessage): void {
        try {
            const session = this.getSession(personaId, sessionId);
            if (!session) {
                console.error(`Session ${sessionId} not found for persona ${personaId}`);
                return;
            }

            session.messages.push(message);

            const sessionStorageKey = this.getSessionStorageKey(personaId, sessionId);
            localStorage.setItem(sessionStorageKey, JSON.stringify(session.messages));

            session.metadata.message_count = session.messages.length;
            session.metadata.updated_at = Date.now();
            session.metadata.last_message_preview = message.content.substring(0, 100);

            if (session.metadata.title === 'New Conversation' && message.role === 'user') {
                session.metadata.title = message.content.substring(0, 50) + (message.content.length > 50 ? '...' : '');
            }

            this.updateSessionMetadata(personaId, session.metadata);
        } catch (error) {
            console.error('Error saving message:', error);
        }
    }

    updateSessionMetadata(personaId: string, updatedMetadata: SessionMetadata): void {
        try {
            const sessions = this.getSessions(personaId);
            const index = sessions.findIndex(s => s.id === updatedMetadata.id);
            
            if (index !== -1) {
                sessions[index] = updatedMetadata;
                const storageKey = this.getSessionsStorageKey(personaId);
                localStorage.setItem(storageKey, JSON.stringify(sessions));
            }
        } catch (error) {
            console.error('Error updating session metadata:', error);
        }
    }

    deleteSession(personaId: string, sessionId: string): void {
        try {
            const sessions = this.getSessions(personaId);
            const filteredSessions = sessions.filter(s => s.id !== sessionId);
            
            const storageKey = this.getSessionsStorageKey(personaId);
            localStorage.setItem(storageKey, JSON.stringify(filteredSessions));

            const sessionStorageKey = this.getSessionStorageKey(personaId, sessionId);
            localStorage.removeItem(sessionStorageKey);

            if (localStorage.getItem('active_session') === sessionId) {
                localStorage.removeItem('active_session');
            }
        } catch (error) {
            console.error('Error deleting session:', error);
        }
    }

    renameSession(personaId: string, sessionId: string, newTitle: string): void {
        try {
            const sessions = this.getSessions(personaId);
            const session = sessions.find(s => s.id === sessionId);
            
            if (session) {
                session.title = newTitle;
                session.updated_at = Date.now();
                this.updateSessionMetadata(personaId, session);
            }
        } catch (error) {
            console.error('Error renaming session:', error);
        }
    }

    clearAllSessions(personaId: string): void {
        try {
            const sessions = this.getSessions(personaId);
            
            sessions.forEach(session => {
                const sessionStorageKey = this.getSessionStorageKey(personaId, session.id);
                localStorage.removeItem(sessionStorageKey);
            });

            const storageKey = this.getSessionsStorageKey(personaId);
            localStorage.removeItem(storageKey);

            if (localStorage.getItem('active_persona') === personaId) {
                localStorage.removeItem('active_persona');
                localStorage.removeItem('active_session');
            }
        } catch (error) {
            console.error('Error clearing all sessions:', error);
        }
    }

    getActivePersona(): string | null {
        return localStorage.getItem('active_persona');
    }

    getActiveSession(): string | null {
        return localStorage.getItem('active_session');
    }

    setActiveSession(personaId: string, sessionId: string): void {
        localStorage.setItem('active_persona', personaId);
        localStorage.setItem('active_session', sessionId);
    }
}

export default new SessionManager();